'use client'

import { ChevronDownIcon, ChevronUpIcon, PlusIcon } from 'lucide-react'

import Link from 'next/link'
import { Fragment } from 'react'

import {
  SidebarItem,
  SidebarList,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui'
import type { MenuItem } from '@/utils/routes/menu'
import { DialogCreatePortfolio } from '../../dialogCreatePortfolio'

type PortfolioMenuProps = {
  activeMenu: string
  isCollapsed: boolean
  pathname: string
  portfolioMenu: MenuItem[]
  setActiveMenu: (menu: string) => void
  hasPermissionToCreatePortfolio: boolean
  isOpenDialogCreatePortfolio: boolean
  setIsOpenDialogCreatePortfolio: (isOpen: boolean) => void
  handleCreatePortfolio: (data: { title: string; description?: string }) => void
  isPending: boolean
}

export const PortfolioMenu = ({
  activeMenu,
  isCollapsed,
  pathname,
  portfolioMenu,
  setActiveMenu,
  hasPermissionToCreatePortfolio,
  isOpenDialogCreatePortfolio,
  setIsOpenDialogCreatePortfolio,
  handleCreatePortfolio,
  isPending,
}: PortfolioMenuProps) => {
  return (
    <>
      {portfolioMenu.map(({ icon: Icon, key, matchPattern, label, items }) => (
        <Fragment key={key}>
          <SidebarItem
            className={!isCollapsed ? 'justify-between' : ''}
            mode="outlined"
            isActive={new RegExp(matchPattern).test(pathname)}
          >
            {isCollapsed ? (
              <Icon />
            ) : (
              <>
                <div className="flex items-center gap-2">
                  <Icon className="size-5 md:size-6" />
                  {label}
                  {hasPermissionToCreatePortfolio && (
                    <DialogCreatePortfolio
                      isOpen={isOpenDialogCreatePortfolio}
                      onOpenChange={setIsOpenDialogCreatePortfolio}
                      handleSubmit={handleCreatePortfolio}
                      isLoading={isPending}
                    />
                  )}
                </div>

                {activeMenu === key ? (
                  <ChevronUpIcon
                    className="size-5 md:size-6"
                    onClick={() => {
                      setActiveMenu(activeMenu === key ? '' : key)
                    }}
                    data-testid="chevron-up"
                  />
                ) : (
                  <ChevronDownIcon
                    className="size-5 md:size-6"
                    onClick={() => {
                      setActiveMenu(activeMenu === key ? '' : key)
                    }}
                    data-testid="chevron-down"
                  />
                )}
              </>
            )}
          </SidebarItem>
          {activeMenu === key && !isCollapsed && (
            <span className="pl-4 text-xs font-semibold uppercase text-system-grey-strong">
              PORTFÓLIOS
            </span>
          )}
          {activeMenu === key && (
            <>
              <SidebarList isSubList>
                {items.map(
                  ({
                    icon: Icon,
                    label,
                    link,
                    disabled,
                    matchPattern,
                    tooltipText,
                  }) => {
                    if (tooltipText) {
                      return (
                        <TooltipProvider key={link}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <SidebarItem
                                isSubItem
                                isActive={new RegExp(matchPattern).test(
                                  pathname,
                                )}
                                disabled={disabled}
                                asChild
                              >
                                <Link href={link}>
                                  {Icon && (
                                    <Icon className="size-5 md:size-6" />
                                  )}
                                  {label}
                                </Link>
                              </SidebarItem>
                            </TooltipTrigger>
                            <TooltipContent>
                              <span className="text-sm font-medium text-system-text-primary">
                                {tooltipText}
                              </span>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )
                    }

                    return (
                      <SidebarItem
                        key={link}
                        isSubItem
                        isActive={new RegExp(matchPattern).test(pathname)}
                        disabled={disabled}
                        asChild
                      >
                        <Link href={link}>
                          {Icon && <Icon className="size-5 md:size-6" />}
                          <span className="w-11/12 truncate">{label}</span>
                        </Link>
                      </SidebarItem>
                    )
                  },
                )}
              </SidebarList>
              {activeMenu && !isCollapsed && hasPermissionToCreatePortfolio && (
                <button
                  className="flex items-center gap-2 pl-4 font-inter font-medium text-system-text-secondary hover:text-system-text-primary"
                  onClick={() => {
                    setIsOpenDialogCreatePortfolio(true)
                  }}
                  type="button"
                >
                  <PlusIcon size={16} />
                  Criar portfólio
                </button>
              )}
            </>
          )}
        </Fragment>
      ))}
    </>
  )
}

PortfolioMenu.displayName = 'PortfolioMenu'
